// The catalog cache: one in-memory copy per tab, backed by IndexedDB.
//
// Cold, it costs one `catalog:sync`. Warm, it costs nothing — which is the whole
// point, because opening the terminal used to be nine to twelve HTTP round trips
// (a full products:list, a products:get fan-out, and settings:get three times over
// from three different components) and every one of them was on the cashier's clock.
//
// Namespaced by STORE, not by store+user: two cashiers sharing a browser are looking
// at the same catalog, and making a shift change re-download it is exactly the wait
// this exists to remove. The sale queue is namespaced per user — see queue.ts, and
// the reason there is different.

import type { CatalogSnapshot } from '../../shared/types'
import { kvDelete, kvGet, kvPut } from './idb'
import { cursorFrom, mergeSnapshot, snapshotChanged, type CatalogState } from './merge'

/** Fetches a snapshot from the server. Injected so this module never imports the transport. */
export type SnapshotFetcher = (since?: string) => Promise<CatalogSnapshot>

interface Persisted {
  state: CatalogState
  cursor: string
}

let storeId: string | null = null
/** Mirror of what is in IndexedDB, so a warm read never awaits a database. */
let live: Persisted | null = null
/** In-flight load or revalidate, so N components mounting at once share one request. */
let inflight: Promise<CatalogState | null> | null = null

function key(id: string): string {
  return `cat:${id}`
}

/**
 * Point the cache at a store. Called when a session is observed, which the shim
 * learns from `auth:session` / `auth:login` rather than by asking — those responses
 * already carry the store id, and an extra round trip to find out where to cache
 * would be self-defeating.
 */
export async function bind(id: string): Promise<void> {
  if (storeId === id) return
  storeId = id
  live = null
  inflight = null
  live = (await kvGet<Persisted>(key(id))) ?? null
}

/** Forget the current store (sign-out, or a switch to another store). */
export async function unbind(): Promise<void> {
  const id = storeId
  storeId = null
  live = null
  inflight = null
  if (id) await kvDelete(key(id))
}

export function boundStore(): string | null {
  return storeId
}

/** What we have right now, without asking the server. Null when cold. */
export function peek(): CatalogState | null {
  return live?.state ?? null
}

async function persist(next: Persisted): Promise<void> {
  live = next
  if (storeId) await kvPut(key(storeId), next)
}

/**
 * Warm the cache if it is cold, and hand back the state.
 *
 * Concurrent callers share one request: on a cold load every screen and provider
 * mounts at once, and without this the first paint would fire five identical full
 * snapshots.
 */
export async function ensure(fetch: SnapshotFetcher): Promise<CatalogState | null> {
  if (live) return live.state
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const snap = await fetch()
      await persist({ state: mergeSnapshot(null, snap), cursor: cursorFrom(snap.serverTime) })
      return live!.state
    } finally {
      inflight = null
    }
  })()
  return inflight
}

/**
 * Ask for what has changed and fold it in. Returns true when something actually
 * moved, which is the signal to tell the screens to re-render.
 *
 * A cold cache revalidates by warming instead — same round trip, and it means a
 * caller never has to check which state it is in.
 */
export async function revalidate(fetch: SnapshotFetcher): Promise<boolean> {
  if (!storeId) return false
  if (!live) return (await ensure(fetch)) !== null
  if (inflight) {
    await inflight
    return false
  }

  const before = live
  let moved = false
  inflight = (async () => {
    try {
      const snap = await fetch(before.cursor)
      moved = snapshotChanged(before.state, snap)
      const merged = mergeSnapshot(before.state, snap)
      await persist({ state: merged, cursor: cursorFrom(snap.serverTime) })
      return merged
    } finally {
      inflight = null
    }
  })()

  await inflight
  return moved
}

/**
 * Replace the whole snapshot. For the moments a delta cannot express: the acting
 * branch changed, so every product's quantity is a different number even though no
 * product row moved on the server.
 */
export async function refresh(fetch: SnapshotFetcher): Promise<void> {
  if (!storeId) return
  if (inflight) await inflight
  inflight = (async () => {
    try {
      const snap = await fetch()
      const merged = mergeSnapshot(null, snap)
      await persist({ state: merged, cursor: cursorFrom(snap.serverTime) })
      return merged
    } finally {
      inflight = null
    }
  })()
  await inflight
}

/** Test seam. */
export function _reset(): void {
  storeId = null
  live = null
  inflight = null
}
